import { ReactNode, useState } from "react";

import { Button, Modal, TextArea } from "@smart/itops-sb-design-system-dom";
import {
  getLocalSmokeballAppContext,
  isDemoMode,
  LocalSmokeballAppContext,
  setLocalSmokeballAppContext,
} from "@smart/itops-smokeball-app-dom";

const TOKEN_KEY = "smart-auth-token";
const ID_TOKEN_KEY = "smart-auth-id-token";

type DemoConfig = {
  token: string;
  idToken: string;
  context: LocalSmokeballAppContext;
};

const hasStoredData = () =>
  !!localStorage.getItem(TOKEN_KEY) &&
  !!localStorage.getItem(ID_TOKEN_KEY) &&
  !!getLocalSmokeballAppContext();

const buildStoredConfig = () =>
  JSON.stringify(
    {
      token: localStorage.getItem(TOKEN_KEY) || "",
      idToken: localStorage.getItem(ID_TOKEN_KEY) || "",
      context: getLocalSmokeballAppContext() || {},
    },
    null,
    2,
  );

type DemoGateProps = { children: ReactNode };

const DemoGate = ({ children }: DemoGateProps) => {
  const isDemo = isDemoMode();
  const [showForm, setShowForm] = useState(() => isDemo && !hasStoredData());
  const [config, setConfig] = useState(buildStoredConfig);
  const [error, setError] = useState<string>();

  if (!isDemo) return children;

  const handleLoad = () => {
    setError(undefined);
    let parsed: Partial<DemoConfig>;
    try {
      parsed = JSON.parse(config);
    } catch {
      setError("Invalid JSON");
      return;
    }
    if (!parsed.token || !parsed.idToken || !parsed.context) {
      setError("JSON must include token, idToken and context");
      return;
    }
    localStorage.setItem(TOKEN_KEY, parsed.token);
    localStorage.setItem(ID_TOKEN_KEY, parsed.idToken);
    setLocalSmokeballAppContext(parsed.context);
    window.location.reload();
  };

  return (
    <>
      {!showForm && children}
      <Modal
        open={showForm}
        variant="scrollable"
        size="large"
        header={{
          text: "Demo Mode Setup",
          onClose: hasStoredData() ? () => setShowForm(false) : undefined,
        }}
        footer={{
          buttons: [
            ...(hasStoredData()
              ? [
                  {
                    key: "cancel",
                    text: "Cancel",
                    variant: "secondaryDefault" as const,
                    onClick: () => setShowForm(false),
                  },
                ]
              : []),
            {
              key: "load",
              text: "Load",
              variant: "primaryDefault" as const,
              onClick: handleLoad,
            },
          ],
        }}
      >
        <div className="flex flex-col gap-medium">
          <TextArea
            textAreaClassName="h-[40rem]"
            label="Demo Config (JSON)"
            value={config}
            onChange={(e) => setConfig(e.target.value)}
            error={!!error}
            message={error}
          />
        </div>
      </Modal>
      {!showForm && (
        <Button
          className="fixed bottom-4 right-4 z-[9999]"
          text="Demo config"
          variant="secondaryDefault"
          size="small"
          onClick={() => setShowForm(true)}
        />
      )}
    </>
  );
};

export { DemoGate };
